<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { vOnClickOutside } from '@vueuse/components';
import { useMapGetter } from 'dashboard/composables/store';
import InboxChannelGlyph from 'dashboard/routes/dashboard/settings/inbox/components/InboxChannelGlyph.vue';

defineProps({
  hasError: { type: Boolean, default: false },
});

const selected = defineModel({
  type: [Array, String],
  default: () => [],
});

const { t } = useI18n();
const open = ref(false);
const searchTerm = ref('');

const inboxes = useMapGetter('inboxes/getInboxes');

const sortedInboxes = computed(() =>
  [...(inboxes.value || [])].sort((a, b) => a.name.localeCompare(b.name))
);

const selectedItems = computed(() => {
  if (!Array.isArray(selected.value) || selected.value.length === 0) {
    return [];
  }
  return selected.value
    .map(item => {
      const inbox = sortedInboxes.value.find(i => i.id === item.id);
      return inbox ? { inbox, id: inbox.id, name: inbox.name } : null;
    })
    .filter(Boolean);
});

const filteredInboxes = computed(() => {
  const q = searchTerm.value.trim().toLowerCase();
  if (!q) return sortedInboxes.value;
  return sortedInboxes.value.filter(inbox =>
    inbox.name.toLowerCase().includes(q)
  );
});

const triggerLabel = computed(() => {
  if (!selectedItems.value.length) {
    return t('AUTOMATION.FLOW.INBOX_SELECT.PLACEHOLDER');
  }
  if (selectedItems.value.length === 1) {
    return selectedItems.value[0].name;
  }
  return t('AUTOMATION.FLOW.INBOX_SELECT.SELECTED_COUNT', {
    count: selectedItems.value.length,
  });
});

const triggerInbox = computed(() => selectedItems.value[0]?.inbox ?? null);

const isSelected = inboxId =>
  Array.isArray(selected.value) &&
  selected.value.some(item => item.id === inboxId);

const close = () => {
  open.value = false;
  searchTerm.value = '';
};

const toggle = () => {
  open.value = !open.value;
  if (!open.value) searchTerm.value = '';
};

watch(
  selected,
  value => {
    if (value === '' || value == null) selected.value = [];
  },
  { immediate: true }
);

const toggleInbox = inbox => {
  const entry = { id: inbox.id, name: inbox.name };
  if (!Array.isArray(selected.value)) {
    selected.value = [entry];
    return;
  }
  if (isSelected(inbox.id)) {
    selected.value = selected.value.filter(item => item.id !== inbox.id);
  } else {
    selected.value = [...selected.value, entry];
  }
};
</script>

<template>
  <div v-on-click-outside="close" class="relative w-full">
    <button
      type="button"
      class="flex w-full items-center gap-2.5 rounded-lg border border-n-weak bg-n-slate-2/70 px-3 py-2.5 text-start transition-colors dark:border-n-strong dark:bg-n-slate-3/30"
      :class="
        hasError
          ? '!border-n-ruby-8 dark:!border-n-ruby-8'
          : open
            ? 'border-n-slate-7 dark:border-n-slate-7'
            : 'hover:border-n-slate-7 dark:hover:border-n-slate-7'
      "
      :aria-label="t('AUTOMATION.FLOW.INBOX_SELECT.PLACEHOLDER')"
      :aria-expanded="open"
      @click="toggle"
    >
      <InboxChannelGlyph
        v-if="triggerInbox"
        :inbox="triggerInbox"
        size="sm"
        class="shrink-0"
      />
      <span
        v-else
        class="inline-flex size-7 shrink-0 items-center justify-center rounded-full bg-n-alpha-2 text-n-slate-11 i-lucide-inbox"
        aria-hidden="true"
      />
      <span
        class="min-w-0 flex-1 truncate text-sm"
        :class="selectedItems.length ? 'text-n-slate-12' : 'text-n-slate-11'"
      >
        {{ triggerLabel }}
      </span>
      <span
        class="inline-flex h-5 w-5 min-h-5 min-w-5 shrink-0 items-center justify-center text-[20px] leading-none text-n-slate-11"
        :class="open ? 'i-lucide-chevron-up' : 'i-lucide-chevron-down'"
        aria-hidden="true"
      />
    </button>

    <div
      v-if="open"
      class="absolute z-50 mt-1 w-full overflow-hidden rounded-xl border border-n-weak bg-n-solid-1 shadow-lg dark:border-n-strong dark:bg-n-solid-2"
      role="listbox"
      aria-multiselectable="true"
    >
      <div class="border-b border-n-weak p-2 dark:border-n-strong">
        <label class="relative block">
          <span
            class="pointer-events-none absolute start-2.5 top-1/2 size-4 -translate-y-1/2 text-n-slate-11 i-lucide-search"
            aria-hidden="true"
          />
          <input
            v-model="searchTerm"
            type="search"
            class="w-full rounded-lg border border-n-weak bg-n-alpha-1 py-2 pe-2.5 ps-9 text-sm text-n-slate-12 outline-none focus:border-n-slate-7 dark:border-n-strong dark:focus:border-n-slate-7"
            :placeholder="t('COMBOBOX.SEARCH_PLACEHOLDER')"
          />
        </label>
      </div>
      <ul class="m-0 max-h-72 list-none overflow-y-auto p-1">
        <li
          v-if="!filteredInboxes.length"
          class="px-2.5 py-3 text-sm text-n-slate-11"
        >
          {{
            searchTerm
              ? t('COMBOBOX.EMPTY_SEARCH_RESULTS', { searchTerm })
              : t('COMBOBOX.EMPTY_STATE')
          }}
        </li>
        <li v-for="inbox in filteredInboxes" :key="inbox.id" role="none">
          <button
            type="button"
            role="option"
            class="flex w-full items-center gap-2.5 rounded-lg px-2.5 py-2.5 text-start transition-colors"
            :class="
              isSelected(inbox.id)
                ? 'bg-n-blue-3 dark:bg-n-blue-4/40'
                : 'hover:bg-n-alpha-2 dark:hover:bg-n-alpha-3'
            "
            :aria-selected="isSelected(inbox.id)"
            @click="toggleInbox(inbox)"
          >
            <InboxChannelGlyph :inbox="inbox" size="sm" class="shrink-0" />
            <span class="min-w-0 flex-1 truncate text-sm text-n-slate-12">
              {{ inbox.name }}
            </span>
            <span
              v-if="isSelected(inbox.id)"
              class="size-4 shrink-0 text-n-blue-11 i-lucide-check"
              aria-hidden="true"
            />
          </button>
        </li>
      </ul>
    </div>
  </div>
</template>
